import { Grid, MenuItem, Select } from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import DeleteIcon from '@mui/icons-material/Delete'
import { differenceInDays, format, parseISO } from 'date-fns'
import { ISO_DATE_FORMAT } from '~/shared/lib/date'
import {
  Box,
  Button,
  IconButton,
  Typography,
  Radio,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Checkbox,
} from '@mui/material'
import { DateAndRelativeDateInput } from '~/shared/ui'
import { useState } from 'react'
import { ProviderDetailWithAdditionalData } from './ProviderListItem'
import { referralSteerageProviders } from '@fireflyhealth/api'

import { EditProviderAndAvailabilityPayload, logEvent } from '~/shared/lib/events'
import { EditCuratedProviderPayload, useEditCuratedProvider } from './utils'
import { useQueryClient } from '@tanstack/react-query'
import { required } from '~/shared/lib/required'

interface IProviderAvailabilityModalProps {
  provider: ProviderDetailWithAdditionalData | null
  handleCloseAvailabilityModal: () => void
  providerSearchType: 'provider' | 'facility' | 'procedure'
  steerageId?: number | null
  isEditedInDirectory?: boolean
  specialtyGroups: { id: number; label: string }[] | null
}

const dateOffset = (dateStr: string): number => {
  const from = new Date()
  from.setHours(0, 0, 0, 0)

  const to = parseISO(dateStr)
  to.setHours(0, 0, 0, 0)

  const dayDifference = differenceInDays(to, from)
  const absDayDifference = Math.abs(dayDifference)

  return absDayDifference
}

export const UpdateProviderAvailabilityModal = (props: IProviderAvailabilityModalProps) => {
  const client = useQueryClient()

  const { mutateAsync: handleEditCuratedProvider } = useEditCuratedProvider()
  const [locationAvailabilitySelected, setLocationAvailability] = useState<boolean>(false)
  const [specialtyGroupId, setSpecialtyGroupId] = useState<number>(0)
  const [availabilityDate, setAvailabilityDate] = useState<string>('')
  const [isSpecialtyGroupSelected, setSelectedSpecialtyGroupFlag] = useState<boolean>(false)
  const [isOptionSelected, setOptionSelected] = useState<boolean>(false)
  const location =
    props?.provider?.locations && props?.provider?.locations.length >= 1
      ? props?.provider?.locations[0]
      : null
  const providerName = props?.provider?.name ?? location?.name
  const careOrgName = props?.provider?.npi ? location?.name : props?.provider?.name
  const specialtyGroups = props?.specialtyGroups
  const specialtyGroupCount = specialtyGroups?.length ?? 0
  const singleSpecialtyGroupId = specialtyGroups?.[0]?.id ?? null
  const address = location?.address_details

  const [doesProviderExist, setDoesProviderExist] = useState<boolean>(
    location?.availability?.does_provider_exist ?? true,
  )
  const [clearAvailability, setClearAvailability] = useState<boolean>(false)

  const createProviderAvailability = async (
    daysTillNextAvailability: number | null,
    addedOn: string | null,
    specialtyGroupId: number,
    doesProviderExist: boolean | null,
  ) => {
    const curatedProviderPayload: EditCuratedProviderPayload = {
      npi: props?.provider?.npi ?? null,
      care_org_name: careOrgName ?? null,
      address_line_1: address?.address_line_1 ?? null,
      address_line_2: address?.address_line_2 ? address?.address_line_2 : null,
      city: address?.city ?? null,
      state: address?.state ?? null,
      zip_code: address?.zip ?? null,
      availability: {
        number_of_days_till_next_availability: daysTillNextAvailability,
        added_at: addedOn,
        specialty_group: specialtyGroupId == 0 ? null : specialtyGroupId,
        does_provider_exist: clearAvailability ? true : (doesProviderExist ?? null),
      },
    }
    return handleEditCuratedProvider(curatedProviderPayload).then(async () => {
      if (props?.steerageId) {
        // invalidate queries for refetching the providers
        await client.cancelQueries({
          queryKey: referralSteerageProviders.listQueryKey({ steerage_id: props?.steerageId ?? 0 }),
        })
        await client.invalidateQueries({
          queryKey: referralSteerageProviders.listQueryKey({ steerage_id: props?.steerageId ?? 0 }),
        })
      }
      // Log availability updated for provider at a location
      void logEvent<EditProviderAndAvailabilityPayload>(
        'UPDATED_AVAILABILITY_PROVIDER_AT_A_LOCATION',
        {
          providerNpi: props?.provider?.npi,
          steerageId: props?.steerageId,
          isEditedInDirectory: props?.isEditedInDirectory,
          doesProviderExist: doesProviderExist ?? null,
          isClearingAvailability: !daysTillNextAvailability && !addedOn ? true : false,
          isProviderSearch: true,
        },
      )
    })
  }

  const createCareOrgAvailability = async (
    daysTillNextAvailability: number | null,
    addedOn: string | null,
    specialtyGroupId: number,
    doesProviderExist: boolean | null,
  ) => {
    // Dual write the curated provider availability information
    const curatedProviderPayload: EditCuratedProviderPayload = {
      npi: props?.provider?.npi ?? null,
      care_org_name: careOrgName ?? null,
      address_line_1: address?.address_line_1 ?? null,
      address_line_2: address?.address_line_2 ? address?.address_line_2 : null,
      city: address?.city ?? null,
      state: address?.state ?? null,
      zip_code: address?.zip ?? null,
      availability: {
        number_of_days_till_next_availability: daysTillNextAvailability,
        added_at: addedOn,
        specialty_group: specialtyGroupId == 0 ? null : specialtyGroupId,
        does_provider_exist: clearAvailability ? true : (doesProviderExist ?? null),
      },
    }
    return handleEditCuratedProvider(curatedProviderPayload).then(async () => {
      if (props?.steerageId) {
        // invalidate queries for refetching the providers
        await client.cancelQueries({
          queryKey: referralSteerageProviders.listQueryKey({ steerage_id: props?.steerageId ?? 0 }),
        })
        await client.invalidateQueries({
          queryKey: referralSteerageProviders.listQueryKey({ steerage_id: props?.steerageId ?? 0 }),
        })
      }
      if (specialtyGroupId == 0) {
        // Log availability updated for all providers at a location
        void logEvent<EditProviderAndAvailabilityPayload>(
          'UPDATED_AVAILABILITY_ALL_PROVIDER_AT_A_LOCATION',
          {
            careOrganisationName: location?.name,
            steerageId: props?.steerageId,
            isEditedInDirectory: props?.isEditedInDirectory,
            doesProviderExist: doesProviderExist ?? null,
            isClearingAvailability: !daysTillNextAvailability && !addedOn ? true : false,
            isProviderSearch: false,
          },
        )
      } else {
        // Log availability updated for all providers at a department
        void logEvent<EditProviderAndAvailabilityPayload>(
          'UPDATED_AVAILABILITY_ALL_PROVIDER_AT_A_DEPARTMENT',
          {
            careOrganisationName: location?.name,
            steerageId: props?.steerageId,
            isEditedInDirectory: props?.isEditedInDirectory,
            doesProviderExist: doesProviderExist ?? null,
            isClearingAvailability: !daysTillNextAvailability && !addedOn ? true : false,
            isProviderSearch: props?.provider?.npi ? true : false,
          },
        )
      }
    })
  }

  // With several specialty groups, a specialty-scoped availability needs one picked first.
  const needsSpecialtyGroupChoice =
    locationAvailabilitySelected &&
    isSpecialtyGroupSelected &&
    specialtyGroupCount > 1 &&
    specialtyGroupId === 0
  const isSaveEnabled =
    isOptionSelected &&
    (doesProviderExist === false || (availabilityDate !== '' && !needsSpecialtyGroupChoice))

  const handleSave = async (daysTillNextAvailability: number | null, addedOn: string | null) => {
    try {
      if (locationAvailabilitySelected) {
        await createCareOrgAvailability(
          daysTillNextAvailability,
          addedOn,
          specialtyGroupId,
          doesProviderExist ?? null,
        )
      } else {
        await createProviderAvailability(
          daysTillNextAvailability,
          addedOn,
          specialtyGroupId,
          doesProviderExist ?? null,
        )
      }
    } catch {
      // The mutation hook surfaces its own error notification; swallow here so
      // the modal still closes and the rejection isn't left unhandled.
    } finally {
      // Close only after the mutation settles, so its state update lands while
      // the modal is still mounted (avoids "setState on unmounted component").
      props.handleCloseAvailabilityModal()
    }
  }

  const practitionerLabel = 'Only ' + providerName + ' at this location'
  const facilityLabel = 'All providers at this location'
  const primaryEntityLabel =
    props.providerSearchType == 'provider' ? practitionerLabel : facilityLabel

  const practitionerSearchAllProviderLabel = 'All providers at this location'
  const selectSpecialtyLabel = 'selected specialty'

  const addSpecialtyEditingOption =
    props.providerSearchType == 'provider' &&
    careOrgName != null &&
    specialtyGroups != null &&
    specialtyGroups.length > 0

  const handleAvailabilityChoice = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isOptionSelected == false) {
      setOptionSelected(true)
    }
    if (event.target.value == practitionerLabel) {
      setLocationAvailability(false)
      setSpecialtyGroupId(0)
      return
    }
    if (event.target.value.toString() == selectSpecialtyLabel.toString()) {
      setSelectedSpecialtyGroupFlag(true)
      if (specialtyGroupCount === 1 && singleSpecialtyGroupId != null) {
        setSpecialtyGroupId(singleSpecialtyGroupId)
      }
    }

    // if all providers are selected set the specialty group to null or zero
    if (event.target.value == practitionerSearchAllProviderLabel) {
      setSelectedSpecialtyGroupFlag(false)
      setSpecialtyGroupId(0)
    }

    setLocationAvailability(true)
  }

  const handleNotAcceptingPatientsOnchange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDoesProviderExist(!event.target.checked)

    if (doesProviderExist) {
      // disable and clear the availability input
      setAvailabilityDate('')
    }
  }

  const handleClearAvailabilityModal = () => {
    setClearAvailability(true)
    // Log clear availability button click
    void logEvent<EditProviderAndAvailabilityPayload>(
      'OPEN_MODAL_FOR_CLEARING_AVAILABILITY_CLICK',
      {
        providerNpi: props?.provider?.npi,
        steerageId: props?.steerageId,
        isEditedInDirectory: props?.isEditedInDirectory,
        doesProviderExist: null,
        isClearingAvailability: true,
        isProviderSearch: props?.provider?.npi ? true : false,
      },
    )
  }
  return (
    <Box>
      {!clearAvailability && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography sx={{ fontWeight: 500, fontSize: '20px', ml: '12px' }}>
              Edit Availability
            </Typography>
            <Box>
              <IconButton
                style={{ padding: '0px' }}
                onClick={handleClearAvailabilityModal}
                size="small"
              >
                <DeleteIcon />
              </IconButton>
              <IconButton
                style={{ padding: '5px' }}
                onClick={props.handleCloseAvailabilityModal}
                size="small"
              >
                <ClearIcon />
              </IconButton>
            </Box>
          </Box>
          <Typography sx={{ fontSize: '16px', mt: '-4px', ml: '12px', mr: 4 }}>
            {providerName}
          </Typography>
          <Box sx={{ mt: 1, m: 2 }}>
            <Typography>Next available appointment</Typography>
            <DateAndRelativeDateInput
              value={availabilityDate}
              onChange={dateString => setAvailabilityDate(dateString)}
              disabled={!doesProviderExist}
              minDate={format(new Date(), ISO_DATE_FORMAT)}
            />
          </Box>
          <Box sx={{ m: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!doesProviderExist}
                  onChange={handleNotAcceptingPatientsOnchange}
                  name="doesProviderExist"
                />
              }
              label="Provider doesn’t exist at this location"
            />
          </Box>
        </>
      )}
      <Box sx={{ m: 2, mt: 3 }}>
        <FormControl>
          {!clearAvailability ? (
            <Typography>Edit availability for:</Typography>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography sx={{ fontWeight: 500 }}>Delete existing availability for:</Typography>
              <Box sx={{ ml: 2 }}>
                <IconButton
                  style={{ padding: '5px' }}
                  onClick={props.handleCloseAvailabilityModal}
                  size="small"
                >
                  <ClearIcon />
                </IconButton>
              </Box>
            </Box>
          )}
          <RadioGroup
            aria-labelledby="demo-radio-buttons-group-label"
            name="radio-buttons-group"
            onChange={handleAvailabilityChoice}
          >
            {/* Add a primary-edition option, which is either the provider for provider
                search or all providers at the location for facility search. Everyone
                gets a first option! */}
            <FormControlLabel
              value={primaryEntityLabel}
              control={<Radio />}
              label={primaryEntityLabel}
            />
            {/* Add a Specialty-editing option, if possible */}
            {addSpecialtyEditingOption ? (
              <FormControlLabel
                value={selectSpecialtyLabel}
                control={<Radio />}
                label={
                  <Grid container direction={'row'} spacing={0.5}>
                    <Grid>All</Grid>
                    {specialtyGroups.length > 1 ? (
                      <Grid>
                        <Select
                          variant="standard"
                          sx={{ maxHeight: '25px', ml: '4px', mr: '5px' }}
                          onChange={e => setSpecialtyGroupId(e.target.value as number)}
                        >
                          {specialtyGroups.map((group, i) => (
                            <MenuItem value={group.id} key={i}>
                              {group.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>
                    ) : (
                      <Grid>{required(specialtyGroups[0], 'the one specialty group').label}</Grid>
                    )}
                    <Grid>providers</Grid>
                    <Grid> at</Grid>
                    <Grid> this</Grid>
                    <Grid> location</Grid>
                  </Grid>
                }
              />
            ) : (
              ''
            )}
          </RadioGroup>
        </FormControl>
      </Box>
      {clearAvailability ? (
        <Box sx={{ my: 1, mx: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={() => setClearAvailability(false)}>
            <Typography>Cancel</Typography>
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!isOptionSelected}
            onClick={() => void handleSave(null, null)}
          >
            <Typography>Delete</Typography>
          </Button>
        </Box>
      ) : (
        <Box sx={{ my: 1, mx: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={props.handleCloseAvailabilityModal}>
            <Typography>Cancel</Typography>
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!isSaveEnabled}
            onClick={() =>
              void handleSave(dateOffset(availabilityDate), format(new Date(), ISO_DATE_FORMAT))
            }
          >
            <Typography>Save</Typography>
          </Button>
        </Box>
      )}
    </Box>
  )
}
